import { Feature } from '@getstep/proto/feature'
import { action, computed, makeObservable, observable } from 'mobx'
import type { useRouter } from 'next/router'

import { BaseVerificationViewModel } from './BaseVerificationViewModel'
import type { PlaybookStore } from './PlaybookStore'
import { URLS } from '@components/playbook/urls'
import analyticsService from '@lib/analytics/AnalyticsService'
import { AnalyticsEvent } from '@lib/analytics/events'
import { localStorage } from '@lib/hooks/useLocalStorage'
import type { SessionStore } from '@lib/store/session/SessionStore'
import type { OtpVerificationStatus } from '@lib/utils/login'
import { LoginStatus, PhoneVerificationStatus } from '@lib/utils/login'
import { isPrd } from '@shared/util/env'
import { testApiFactory } from '@shared/util/tests/factory'

// Storage key for test feature flags (must match phone-verification component)
const TEST_FEATURE_FLAGS_STORAGE_KEY = 'storage.TestFeatureFlagsToEnable'

export class PhoneVerificationViewModel extends BaseVerificationViewModel {
    // Phone-specific state
    isConsentChecked = true
    testFeatureFlagsToEnable: Feature[] = []

    constructor(
        router: ReturnType<typeof useRouter>,
        searchParams: URLSearchParams,
        playbook: PlaybookStore,
        sessionStore: SessionStore,
    ) {
        super(router, searchParams, playbook, sessionStore)

        // Load test feature flags from localStorage
        if (!isPrd() && typeof window !== 'undefined') {
            this.testFeatureFlagsToEnable = localStorage.getItem<Feature[]>(
                TEST_FEATURE_FLAGS_STORAGE_KEY,
                [],
            )
        }

        makeObservable(this, {
            isConsentChecked: observable,
            testFeatureFlagsToEnable: observable,
            setConsentChecked: action,
            setTestFeatureFlagsToEnable: action,
            successRedirectUrl: computed,
            backUrl: computed,
            inputValidationError: computed,
            verificationType: computed,
        })
    }

    // Additional getters for the UI
    get canResendCodeFromStore(): boolean {
        return this.sessionStore.phoneVerificationStore?.canResendCode ?? true
    }

    get phoneNumberDisplay(): string {
        return (
            this.sessionStore.phoneVerificationStore?.valueToVerify ||
            this.inputValue
        )
    }

    get successRedirectUrl(): string {
        // For synchronous computed, return default and let async navigation handle the logic
        return URLS.EMAIL_VERIFICATION
    }

    get backUrl(): string {
        return URLS.EXCLUSIVE_OFFER
    }

    get inputValidationError(): string {
        if (!this.isConsentChecked) {
            return 'Please consent to receive SMS marketing messages to continue'
        }
        return 'Please enter a valid 10-digit phone number'
    }

    get verificationType(): string {
        return 'phone'
    }

    async getSuccessRedirectUrl(): Promise<string> {
        // Recovery users should always use normal flow
        if (this.sessionStore.recoveryStore) {
            return URLS.EMAIL_VERIFICATION
        }

        const isKycPrefillEnabled =
            this.sessionStore.authorizedStore?.featureStore?.isEnabled(
                Feature.KYC_PREFILL,
            ) ?? false
        if (isKycPrefillEnabled) {
            return URLS.KYC_PREFILL
        }
        return URLS.EMAIL_VERIFICATION
    }

    // Phone-specific actions
    setConsentChecked(checked: boolean) {
        this.isConsentChecked = checked
        if (this.hasError && checked) {
            this.clearError()
        }
    }

    setTestFeatureFlagsToEnable(flags: Feature[]) {
        this.testFeatureFlagsToEnable = flags
    }

    // Implement abstract methods
    protected validateInput(value: string): boolean {
        const digits = value.replace(/\D/g, '')
        return digits.length === 10
    }

    protected formatInput(value: string): string {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '')

        // Limit to 10 digits
        const limitedDigits = digits.slice(0, 10)

        // Format as (XXX) XXX-XXXX
        if (limitedDigits.length >= 6) {
            return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(
                3,
                6,
            )}-${limitedDigits.slice(6)}`
        } else if (limitedDigits.length >= 3) {
            return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
        } else if (limitedDigits.length > 0) {
            return `(${limitedDigits}`
        }
        return ''
    }

    protected async sendVerificationCode(): Promise<void> {
        const phoneVerificationStore = this.sessionStore.phoneVerificationStore

        if (!phoneVerificationStore) {
            throw new Error('Verification service unavailable')
        }

        const result = await phoneVerificationStore.startVerification(
            this.inputValue,
        )

        switch (result.status) {
            case PhoneVerificationStatus.SUCCESS:
                // Auto-login successful, redirect to next step
                const loginResult = await this.sessionStore.login()
                if (loginResult === LoginStatus.UNSUPPORTED_FACTOR) {
                    await this.navigate(URLS.UNSUPPORTED_AUTH_METHOD)
                    return
                }
                if (loginResult === LoginStatus.IN_RECOVERY) {
                    // Existing user in recovery - use normal flow, not KYC_PREFILL
                    await this.navigate(URLS.EMAIL_VERIFICATION)
                    return
                }
                if (loginResult !== LoginStatus.SUCCESS) {
                    // Any other login status (FAIL, NO_USER, etc.) - use normal flow
                    await this.navigate(URLS.EMAIL_VERIFICATION)
                    return
                }
                // New user with successful login - check if KYC_PREFILL feature is enabled
                const successUrl = await this.getSuccessRedirectUrl()
                await this.navigate(successUrl)
                break

            case PhoneVerificationStatus.SECOND_FACTOR_REQUIRED:
                // Need to verify SMS code - this is the normal flow
                this.sessionStore.otpSessionId = result.otpSessionId
                // Success - the base class will handle step transition
                break

            case PhoneVerificationStatus.NOT_SUPPORTED:
                throw new Error(
                    'Phone verification is not supported for this number',
                )

            case PhoneVerificationStatus.ERROR:
                throw new Error(
                    'Failed to send verification code. Please try again.',
                )

            default:
                throw new Error('An unexpected error occurred')
        }
    }

    protected async verifyCode(code: string): Promise<OtpVerificationStatus> {
        const phoneVerificationStore = this.sessionStore.phoneVerificationStore

        if (!phoneVerificationStore) {
            throw new Error('Verification service unavailable')
        }

        const result = await phoneVerificationStore.completeVerification(code)
        return result.status
    }

    protected async resendVerificationCode(): Promise<void> {
        const phoneVerificationStore = this.sessionStore.phoneVerificationStore

        if (!phoneVerificationStore || !this.inputValue) {
            throw new Error('Cannot resend code')
        }

        const result = await phoneVerificationStore.startVerification(
            this.inputValue,
        )

        if (
            result.status === PhoneVerificationStatus.ERROR ||
            result.status === PhoneVerificationStatus.NOT_SUPPORTED
        ) {
            throw new Error('Failed to resend code. Please try again.')
        }
    }

    // Override verification success to include phone number in analytics
    protected async handleVerificationSuccess(): Promise<void> {
        const loginResult = await this.sessionStore.login()

        if (loginResult === LoginStatus.UNSUPPORTED_FACTOR) {
            await this.navigate(URLS.UNSUPPORTED_AUTH_METHOD)
            return
        }

        if (loginResult === LoginStatus.NOT_ACCOUNT_OWNER) {
            await this.navigate(URLS.NON_ACCOUNT_OWNER_ERROR)
            return
        }

        if (loginResult === LoginStatus.IN_RECOVERY) {
            // Existing user in recovery - use normal flow, not KYC_PREFILL
            await this.navigate(URLS.EMAIL_VERIFICATION)
            return
        }

        if (loginResult !== LoginStatus.SUCCESS) {
            // Any other login status (FAIL, NO_USER, etc.) - use normal flow
            await this.navigate(URLS.EMAIL_VERIFICATION)
            return
        }

        // Apply test feature flags after successful login (dev only)
        if (!isPrd() && this.testFeatureFlagsToEnable.length > 0) {
            try {
                const userId =
                    this.sessionStore.authorizedStore?.userStore?.user?.id
                if (userId) {
                    // Enable each feature on the server via test API
                    for (const feature of this.testFeatureFlagsToEnable) {
                        await testApiFactory.testApi.enableFeature(
                            feature,
                            userId,
                        )
                    }

                    // Refresh feature store to load the newly enabled flags
                    await this.sessionStore.authorizedStore?.featureStore?.refresh()

                    // Clear the test flags from localStorage after applying
                    localStorage.removeItem(TEST_FEATURE_FLAGS_STORAGE_KEY)
                }
            } catch (e) {
                console.error('Failed to apply test feature flags:', e)
                // Continue with normal flow even if flag application fails
            }
        }

        // Set phone number in analytics
        await analyticsService.setUserAttributes({
            phone: this.inputValue,
        })

        // Track successful verification
        await this.trackEvent(AnalyticsEvent.PHONE_VERIFICATION_SUCCESSFUL, {
            phone: this.inputValue,
        })

        // New user with successful login - check if KYC_PREFILL feature is enabled to determine next step
        const successUrl = await this.getSuccessRedirectUrl()
        this.navigate(successUrl)
    }
}
